import type { ChartDisplayMode } from './elecNonresidentialChartSegments';
import { BURBANK_GEOID } from '../../eia861UtilityElectricity/bwpObservations';
import { GAS_EMISSION_FACTOR_MT_PER_THERM } from './bhSourceControls';
import type { ActivityEstimate, CanonicalEnergyActivityYear } from './types';

function burbankCommercialGasEstimate(row: CanonicalEnergyActivityYear): ActivityEstimate {
  return row.nonresidential;
}

function industrialSegmentValue(row: CanonicalEnergyActivityYear): number | null {
  const v = row.industrial.value;
  if (v == null || v <= 0) return null;
  if (row.geoid === BURBANK_GEOID) {
    return row.industrial.methodId.startsWith('burbank_gas_ind_socal') ? v : null;
  }
  return row.industrial.coverage === 'complete_observed' ? v : null;
}

export interface GasComIndStackSegments {
  commercial: number | null;
  industrial: number | null;
}

function roundDisplay(n: number, mode: ChartDisplayMode): number {
  if (mode === 'emissions') return Math.round(n * 10) / 10;
  return Math.round(n);
}

function gasComIndYearEligible(row: CanonicalEnergyActivityYear): boolean {
  if (row.fuel !== 'natural_gas') return false;
  const commercial = row.geoid === BURBANK_GEOID ? burbankCommercialGasEstimate(row) : row.commercial;
  const comTherms = commercial.value;
  if (comTherms == null || comTherms <= 0) return false;
  switch (commercial.coverage) {
    case 'cap_published':
    case 'complete_observed':
    case 'interpolated':
    case 'estimated_complete':
    case 'extrapolated':
      return true;
    default:
      return false;
  }
}

export function buildGasComIndStackSegments(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode
): GasComIndStackSegments {
  if (!gasComIndYearEligible(row)) {
    return { commercial: null, industrial: null };
  }
  const commercial = row.geoid === BURBANK_GEOID ? burbankCommercialGasEstimate(row) : row.commercial;
  const comTherms = commercial.value!;
  const indTherms = industrialSegmentValue(row);
  const hasInd = indTherms != null && indTherms > 0;
  const factor = GAS_EMISSION_FACTOR_MT_PER_THERM;
  if (mode === 'emissions') {
    return {
      commercial: roundDisplay(comTherms * factor, mode),
      industrial: hasInd ? roundDisplay(indTherms! * factor, mode) : null,
    };
  }
  return {
    commercial: roundDisplay(comTherms, mode),
    industrial: hasInd ? roundDisplay(indTherms!, mode) : null,
  };
}
